import React from 'react'
import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import {
    DocumentApi,
    DocumentQuestionUploadStatus,
} from '../../../../services/DocumentApi.service'
import {
    QuestionType,
    UploadedDocumentContent,
} from '../types'

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx']

type UploadStatus = 'uploading' | 'uploaded' | 'processing' | 'failed' | 'processed'

interface UploadItem {
    id: string
    fileName: string
    status: UploadStatus
    document?: UploadedDocumentContent
    error?: string
}

interface Colors {
    botaoFormulario: string
}

interface DocumentUploadQuestionProps {
    question: QuestionType
    projectId: string
    value: UploadedDocumentContent[]
    api: DocumentApi
    colors: Colors
    onChange: (id: string, value: UploadedDocumentContent[]) => void
    onPendingChange: (id: string, pending: boolean) => void
    onProcessUpload?: (id: string, documents: UploadedDocumentContent[]) => void
}

const createItemId = (fileName: string, index: number) =>
    `${fileName}-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`

const getExtension = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf('.')
    return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : ''
}

const validateFile = (file: File): string | null => {
    if (!ACCEPTED_EXTENSIONS.includes(getExtension(file.name))) {
        return 'Formato invalido. Envie apenas PDF ou DOCX.'
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
        return 'Arquivo maior que 20MB.'
    }

    return null
}

// 'uploaded' = arquivo no S3 aguardando a extracao da Lambda document-uploader.
// Para a UI equivale a 'processing' (exibe "Processando...", marca o item como
// pendente e bloqueia o "Proximo") ate o callback gravar o content e o status
// virar 'processed'. Normalizamos num unico ponto para nao espalhar a regra.
const resolveDocumentStatus = (
    status?: UploadStatus,
    content?: string | null
): UploadStatus => {
    const resolved =
        status || (content && content.trim() ? 'processed' : 'uploaded')
    return resolved === 'uploaded' ? 'processing' : resolved
}

const createUploadedItem = (
    document: UploadedDocumentContent,
    index: number
): UploadItem => ({
    id: `${document.document_id || document.filepath}-${index}`,
    fileName: document.document_name,
    status: resolveDocumentStatus(document.status, document.content),
    document,
    error: document.error || undefined,
})

const getDocumentsSignature = (documents: UploadedDocumentContent[]) =>
    documents
        .map(
            (document) =>
                [
                    document.document_id,
                    document.document_name,
                    document.filepath,
                    document.status,
                    document.error,
                    document.content,
                ].join('|')
        )
        .join('\n')

const getUploadedDocuments = (items: UploadItem[]) =>
    items
        .filter((item) => item.document)
        .map((item) => item.document as UploadedDocumentContent)

const isUploadPending = (status: UploadStatus) =>
    status === 'uploading' || status === 'processing'

const getStatusText = (item: UploadItem) => {
    if (item.status === 'uploading') {
        return 'Enviando...'
    }

    if (item.status === 'processing') {
        return 'Processando...'
    }

    if (item.status === 'uploaded') {
        return 'Enviado'
    }

    if (item.status === 'processed') {
        return 'Processado'
    }

    return item.error || 'Falha ao processar o arquivo.'
}

export default function DocumentUploadQuestion({
    question,
    projectId,
    value,
    api,
    colors,
    onChange,
    onPendingChange,
    onProcessUpload,
}: DocumentUploadQuestionProps) {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const [items, setItems] = React.useState<UploadItem[]>(() =>
        value.map(createUploadedItem)
    )
    const valueSignature = React.useMemo(
        () => getDocumentsSignature(value),
        [value]
    )

    React.useEffect(() => {
        setItems((currentItems) => {
            const currentSignature = getDocumentsSignature(
                getUploadedDocuments(currentItems)
            )

            if (currentSignature === valueSignature) {
                return currentItems
            }

            return value.map(createUploadedItem)
        })
    }, [question.id, value, valueSignature])

    React.useEffect(() => {
        const uploadedDocuments = getUploadedDocuments(items)

        onChange(question.id, uploadedDocuments)
        onPendingChange(
            question.id,
            items.some((item) => isUploadPending(item.status))
        )
    }, [items, question.id])

    React.useEffect(() => {
        return () => onPendingChange(question.id, false)
    }, [question.id])

    const openFilePicker = () => {
        inputRef.current?.click()
    }

    const setItemStatus = (
        itemId: string,
        status: DocumentQuestionUploadStatus
    ) => {
        setItems((currentItems) =>
            currentItems.map((item) =>
                item.id === itemId
                    ? {
                          ...item,
                          status,
                      }
                    : item
            )
        )
    }

    const uploadFile = async (itemId: string, file: File) => {
        try {
            const document = await api.uploadQuestionDocument(
                projectId,
                question.id,
                file,
                (status) => setItemStatus(itemId, status)
            )

            setItems((currentItems) =>
                currentItems.map((item) =>
                    item.id === itemId
                        ? {
                              ...item,
                              fileName: document.document_name,
                              status: resolveDocumentStatus(
                                  document.status,
                                  document.content
                              ),
                              document,
                              error: undefined,
                          }
                        : item
                )
            )

            // Build the docs list from `value` (latest formData prop) + this new doc.
            // Cannot rely on setItems callback side-effect: React runs updaters lazily
            // during render, so the captured variable would still be [] here.
            const uploadedDocuments = [
                ...value.filter((d) => d.document_id !== document.document_id),
                document,
            ]
            onProcessUpload?.(question.id, uploadedDocuments)
        } catch (error) {
            setItems((currentItems) =>
                currentItems.map((item) =>
                    item.id === itemId
                        ? {
                              ...item,
                              status: 'failed',
                              error: 'Falha ao processar o arquivo.',
                          }
                        : item
                )
            )
        }
    }

    const handleFiles = (fileList: FileList | File[]) => {
        const selectedFiles = Array.from(fileList)
        const itemsToUpload: Array<{item: UploadItem; file: File}> = []
        const nextItems = selectedFiles.map((file, index) => {
            const error = validateFile(file)
            const item: UploadItem = {
                id: createItemId(file.name, index),
                fileName: file.name,
                status: error ? 'failed' : 'uploading',
                error: error || undefined,
            }

            if (!error) {
                itemsToUpload.push({item, file})
            }

            return item
        })

        setItems((currentItems) => [...currentItems, ...nextItems])
        itemsToUpload.forEach(({item, file}) => uploadFile(item.id, file))
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            handleFiles(event.target.files)
            event.target.value = ''
        }
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (event.dataTransfer.files.length > 0) {
            handleFiles(event.dataTransfer.files)
            event.dataTransfer.clearData()
        }
    }

    const removeItem = (itemId: string) => {
        setItems((currentItems) =>
            currentItems.filter((item) => item.id !== itemId)
        )
    }

    return (
        <Stack spacing={2}>
            <Box
                onClick={openFilePicker}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                sx={{
                    alignItems: 'center',
                    border: '2px dashed #cfd8dc',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    justifyContent: 'center',
                    minHeight: '220px',
                    p: 3,
                    textAlign: 'center',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                        borderColor: colors.botaoFormulario,
                    },
                }}
            >
                <input
                    ref={inputRef}
                    aria-label="Selecionar documentos"
                    data-testid="document-upload-input"
                    type="file"
                    accept=".pdf,.docx"
                    multiple
                    hidden
                    onChange={handleInputChange}
                />
                <CloudUploadIcon
                    sx={{color: colors.botaoFormulario, fontSize: 40}}
                />
                <Typography variant="h6">
                    Arraste e solte seu arquivo aqui
                </Typography>
                <Typography color="#5E5E5E" variant="body2">
                    Arquivos suportados: PDF e DOCX
                </Typography>
                <Button
                    variant="contained"
                    onClick={(event) => {
                        event.stopPropagation()
                        openFilePicker()
                    }}
                    sx={{
                        backgroundColor: '#E1E1E1',
                        color: '#5E5E5E',
                        mt: 1,
                        '&:hover': {
                            backgroundColor: '#D5D5D5',
                        },
                    }}
                >
                    Escolher arquivo
                </Button>
                <Typography color="#5E5E5E" variant="body2">
                    Tamanho maximo: 20MB
                </Typography>
            </Box>

            {items.length > 0 && (
                <Stack aria-label="Documentos enviados" spacing={1}>
                    {items.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                p: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <InsertDriveFileIcon color="action" />
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={item.fileName}
                                    >
                                        {item.fileName}
                                    </Typography>
                                    <Typography
                                        color={
                                            item.status === 'failed'
                                                ? 'error'
                                                : 'text.secondary'
                                        }
                                        variant="body2"
                                    >
                                        {getStatusText(item)}
                                    </Typography>
                                </Box>
                                {(item.status === 'uploaded' ||
                                    item.status === 'processed') && (
                                    <CheckCircleIcon color="success" />
                                )}
                                {item.status === 'failed' && (
                                    <ErrorOutlineIcon color="error" />
                                )}
                                <IconButton
                                    aria-label={`Remover ${item.fileName}`}
                                    onClick={() => removeItem(item.id)}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {isUploadPending(item.status) && (
                                <LinearProgress sx={{mt: 1}} />
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}
